import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import { CreateWalletComponent } from '../../../../companies/create-wallet/create-wallet.component';
import { CreateWalletRequest } from '../../../../companies/interfaces/createWalletRequest';
import { AddTripComponent } from '../../../../components/add-trip/add-trip.component';
import { AssignToTripComponent } from '../../../../components/assign-trip/assign-trip.component';
import { ConfirmDialogComponent } from '../../../../components/confirm-dialog/confirm-dialog.component';
import { CreateTripAndSendComponent } from '../../../../components/create-trip-and-send/create-trip-and-send.component';
import { DispatchComponent } from '../../../../components/dispatch/dispatch.component';
import { NewDiscountComponent } from '../../../../components/new-price/new-price.component';
import { OfferDiscountComponent } from '../../custom-modals/components/offer-discount/offer-discount.component';
import { DISPATCH_STATUS, ROLES_STATUS } from '../../../../enums';
import { CompanyDTO, OrderDTO, QuoteDTO, TripDTO } from '../../../../interfaces/models';
import { WalletDTO } from '../../../../interfaces/walletDTO';
import { CompanyService } from '../../../../providers/company/company.service';
import { LeadService } from '../../../../providers/leads/lead.service';
import { LoadingService } from '../../../../providers/loading/loading.service';
import { NotificationService } from '../../../../providers/notification/notification.service';
import { OrderService } from '../../../../providers/order/order.service';
import { QuoteOfferService } from '../../../../providers/quote-offer/quote-offer.service';
import { QuoteService } from '../../../../providers/quote/quote.service';
import { TripService } from '../../../../providers/trip/trip.service';
import { switchMapAndTrack } from '../../../rxjs/switch-map-and-track.operator';
import { ModalConfigurationStoreService } from '../../../services/modal-configuration-store.service';
import { AddQuoteNoteModalComponent } from '../../custom-modals/components/add-quote-note-modal/add-quote-note-modal.component';
import { TableEventType } from '../models/enums/action-type.enum';
import { TableConfigurationService } from './table-configuration.service';
import { TableGlobalEventService } from './table-global-event.service';

@Injectable()
export class TableActionService {

  constructor(
    private readonly dialog: MatDialog,
    private readonly leadService: LeadService,
    private readonly quoteService: QuoteService,
    private readonly notificationService: NotificationService,
    private readonly tableConfigurationService: TableConfigurationService,
    private readonly tableGlobalEventService: TableGlobalEventService,
    private readonly modalConfigurationStoreService: ModalConfigurationStoreService,
    private readonly companyService: CompanyService,
    private readonly loadingService: LoadingService,
    private readonly orderService: OrderService,
    private readonly tripService: TripService,
    private readonly quoteOfferService: QuoteOfferService,
    private readonly location: Location,
  ) {
  }

  get tableConfig() {
    return this.tableConfigurationService.tableConfig;
  }

  get modalConfig() {
    return this.modalConfigurationStoreService.modals;
  }

  public sentEmail(id: number) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        action: 'Confirm Sent',
        description: 'Are you sure?',
        nameButton: 'Confirm',
        reason: false,
      },
    });

    dialogRef.afterClosed().pipe(
      filter(x => !!x),
      switchMapAndTrack(
        () => this.leadService.sendEmail([id]),
        'configurable-table-loading',
        this.tableConfig.loader,
      ),
      tap(() => this.tableGlobalEventService.sendEvent(TableEventType.GlobalReloadData)),
    ).subscribe();
  }


  public addNote(id: number) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        action: 'Lead notes',
        description: '',
        nameButton: 'Save',
        reason: true,
        placeholder: 'Notes',
        maxlength: 120,
      },
    });

    dialogRef.afterClosed().pipe(
      filter(x => !!x),
      switchMapAndTrack(
        (result: string) => this.leadService.editLead(id, { notes: result }),
        'configurable-table-loading',
        this.tableConfig.loader,
      ),
    ).subscribe();
  }

  public newOfferDiscount(id: number, price: number) {
    this.dialog.open(NewDiscountComponent, {
      data: { id, price },
      width: '450px',
    }).afterClosed().pipe(
      take(1),
      filter(x => !!x),
      tap(() => this.tableConfigurationService.sendEvent(TableEventType.ReloadData)),
    ).subscribe();
  }

  public offerDiscountByCarrierOrDispatcher(quote: QuoteDTO) {
    const {
      id,
      initialPrice: price,
      pickDate,
      deliveryDate,
    } = quote;

    this.dialog.open(OfferDiscountComponent, {
      data: {
        price,
        pickDate,
        deliveryDate,
      },
    }).afterClosed().pipe(
      take(1),
      filter(x => !!x),
      switchMapAndTrack(
        ({ discount, deliveryDate, pickDate }: { discount: number, deliveryDate: Date, pickDate: Date }) =>
          this.quoteOfferService.offerDiscount({
            id,
            discount,
            deliveryDate,
            pickDate
          }),
        'configurable-table-loading',
        this.tableConfig.loader,
      ),
      tap(() => this.tableConfigurationService.sendEvent(TableEventType.ReloadData)),
    ).subscribe();
  }

  public removeQuoteCompletely(id: number) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        action: 'Confirm delete',
        description: 'This Operation will permanently delete this quote from your account. Would you like to continue?',
        nameButton: 'Delete',
        reason: false,
      },
    });

    dialogRef.afterClosed().pipe(
      filter(result => !!result),
      switchMapAndTrack(
        () => this.quoteService.deleteQuoteCompletely(id),
        'configurable-table-loading',
        this.tableConfig.loader,
      ),
      tap(() => this.tableConfigurationService.sendEvent(TableEventType.ReloadData)),
      tap(() => this.notificationService.success('Quote was removed successfully!')),
    ).subscribe();
  }

  public bookOrder(order: OrderDTO, wallet: WalletDTO, company: CompanyDTO) {
    if (!wallet || !wallet.details) {
      this.dialog.open(ConfirmDialogComponent, this.modalConfig.static.walletToAdd).afterClosed().pipe(
        take(1),
        filter(modalResult => !!modalResult),
        switchMap(() => this.dialog.open(CreateWalletComponent, this.modalConfig.dynamic.createWallet(company)).afterClosed()),
        filter(wallet => !!wallet),
        tap(() => this.loadingService.startLoading()),
        switchMap((wallet: CreateWalletRequest) => this.companyService.createWallet(wallet)),
        tap(() => this.loadingService.stopLoading()),
        switchMap(() => this.dialog.open(ConfirmDialogComponent, this.modalConfig.static.walletAdded).afterClosed()),
        filter(modalResult => !!modalResult),
        switchMap(() => this.dialog.open(DispatchComponent, this.modalConfig.dynamic.dispatchOrder(order)).afterClosed()),
        filter(modalResult => !!modalResult),
        tap(() => this.tableGlobalEventService.sendEvent(TableEventType.GlobalReloadData)),
      ).subscribe();
      return;
    }

    this.dialog.open(DispatchComponent, this.modalConfig.dynamic.dispatchOrder(order)).afterClosed().pipe(
      take(1),
      filter(modalResult => !!modalResult),
      tap(() => this.tableGlobalEventService.sendEvent(TableEventType.GlobalReloadData)),
    ).subscribe();
  }

  public cancelRequestedOrder(order: OrderDTO) {
    this.dialog.open(ConfirmDialogComponent, this.modalConfig.static.cancelRequestedOrder).afterClosed().pipe(
      take(1),
      filter(modalResult => !!modalResult),
      switchMapAndTrack(
        () => this.orderService.cancelLoadBoard(order.id),
        'configurable-table-loading',
        this.tableConfig.loader,
      ),
      tap(() => this.tableGlobalEventService.sendEvent(TableEventType.GlobalReloadData)),
    ).subscribe();
  }

  public requestDifferentTerms(order: OrderDTO) {
    const dispatch = order.dispatches ? order.dispatches.find(item => item.status === DISPATCH_STATUS.NEW) : null;
    if (!dispatch) {
      return;
    }

    this.dialog.open(DispatchComponent, this.modalConfig.dynamic.requestDifferentTerms(order, dispatch)).afterClosed().pipe(
      take(1),
      filter(modalResult => !!modalResult),
      tap(() => this.tableGlobalEventService.sendEvent(TableEventType.GlobalReloadData)),
    ).subscribe();
  }

  public createTrip(order: OrderDTO) {
    const { tripId, driverId, dispatcherId } = order;
    let data: Record<string, unknown> = { orderIds: [order.id] };
    if (tripId) {
      data = {
        ...data,
        driverId,
        dispatcherId,
        id: tripId,
      };
    }

    this.dialog.open(CreateTripAndSendComponent, {
      data,
      width: '450px',
    }).afterClosed().pipe(
      take(1),
      filter(result => !!result),
      tap(() => this.tableGlobalEventService.sendEvent(TableEventType.GlobalReloadData)),
    ).subscribe();
  }

  public addTrip(order: OrderDTO) {
    this.dialog.open(AddTripComponent, this.modalConfig.dynamic.addTrip(order)).afterClosed().pipe(
      take(1),
      filter(result => !!result),
      tap(() => this.tableGlobalEventService.sendEvent(TableEventType.GlobalReloadData)),
    ).subscribe();
  }

  public cancelOrder(order: OrderDTO) {
    this.dialog.open(ConfirmDialogComponent, this.modalConfig.static.cancelOrder).afterClosed().pipe(
      take(1),
      filter(result => !!result),
      switchMapAndTrack(
        () => this.orderService.cancleOrder(order.id),
        'configurable-table-loading',
        this.tableConfig.loader,
      ),
      tap(() => this.tableConfigurationService.sendEvent(TableEventType.ReloadData)),
    ).subscribe();
  }

  public removeOrderCompletely(order: OrderDTO) {
    this.dialog.open(ConfirmDialogComponent, this.modalConfig.static.deleteCompletelyOrder).afterClosed().pipe(
      filter(result => !!result),
      switchMapAndTrack(
        () => this.orderService.deleteCompletely(order.id),
        'configurable-table-loading',
        this.tableConfig.loader,
      ),
      tap(() => this.tableConfigurationService.sendEvent(TableEventType.ReloadData)),
      tap(() => this.notificationService.success('Order was removed successfully!')),
    ).subscribe();
  }

  public removeTrip(trip: TripDTO) {
    this.dialog.open(ConfirmDialogComponent, this.modalConfig.static.deleteTrip).afterClosed().pipe(
      take(1),
      filter(result => !!result),
      switchMapAndTrack(
        () => this.tripService.delete([trip.id]),
        'configurable-table-loading',
        this.tableConfig.loader,
      ),
      tap(() => this.tableConfigurationService.sendEvent(TableEventType.ReloadData)),
    ).subscribe();
  }

  public assignDispatcher(trip: TripDTO) {
    const { id, dispatcherId } = trip;
    const data = {
      id,
      accountId: dispatcherId,
      role: ROLES_STATUS.DISPATCHER
    };

    this.dialog.open(AssignToTripComponent, {
      data,
      width: "450px"
    }).afterClosed().pipe(
      take(1),
      filter(result => !!result),
      tap(() => this.tableConfigurationService.sendEvent(TableEventType.ReloadData)),
    ).subscribe();
  }

  public assignDriver(trip: TripDTO) {
    const { id, driverId } = trip;
    const data = {
      id,
      accountId: driverId,
      role: ROLES_STATUS.DRIVER
    };

    this.dialog.open(AssignToTripComponent, {
      data,
      width: "450px"
    }).afterClosed().pipe(
      take(1),
      filter(result => !!result),
      tap(() => this.tableConfigurationService.sendEvent(TableEventType.ReloadData)),
    ).subscribe();
  }

  public unpublish(order: OrderDTO) {
    this.dialog.open(ConfirmDialogComponent, this.modalConfig.static.unpublish).afterClosed().pipe(
      take(1),
      filter(result => !!result),
      switchMapAndTrack(
        () => this.orderService.publish(order.id, 'unpublish'),
        'configurable-table-loading',
        this.tableConfig.loader,
      ),
      tap(() => this.tableConfigurationService.sendEvent(TableEventType.ReloadData)),
    ).subscribe();
  }

  public publish(order: OrderDTO) {
    this.dialog.open(ConfirmDialogComponent, this.modalConfig.static.publish).afterClosed().pipe(
      take(1),
      filter(result => !!result),
      switchMapAndTrack(
        () => this.orderService.publish(order.id, 'publish'),
        'configurable-table-loading',
        this.tableConfig.loader,
      ),
      tap(() => this.tableConfigurationService.sendEvent(TableEventType.ReloadData)),
    ).subscribe();
  }

  public removeOrderFromTrip(tripId: number, orderId: number) {
    this.dialog.open(ConfirmDialogComponent, this.modalConfig.static.deleteOrderFromTrip).afterClosed().pipe(
      take(1),
      filter(result => !!result),
      switchMapAndTrack(
        () => this.tripService.removeOrders(tripId, [orderId]),
        'configurable-table-loading',
        this.tableConfig.loader,
      ),
      tap(() => this.tableConfigurationService.sendEvent(TableEventType.ReloadData)),
    ).subscribe();
  }

  public removeOrderFromTripCompletely(tripId: number, orderId: number) {
    this.dialog.open(ConfirmDialogComponent, this.modalConfig.static.deleteOrderFromTripCompletely).afterClosed().pipe(
      take(1),
      filter(result => !!result),
      switchMapAndTrack(
        () => this.tripService.removeOrders(tripId, [orderId]),
        'configurable-table-loading',
        this.tableConfig.loader,
      ),
      tap(() => this.location.back()),
    ).subscribe();
  }

  public importOrderToTrip(file: File, tripId: string) {
    of({}).pipe(
      take(1),
      switchMapAndTrack(
        () => this.orderService.importOrderToTrip(file, tripId),
        'configurable-table-loading',
        this.tableConfig.loader,
      ),
      tap(() => this.tableConfigurationService.sendEvent(TableEventType.ReloadData))
    ).subscribe();
  }

  public addQuoteNote(quoteId: number) {
    this.dialog.open(AddQuoteNoteModalComponent, this.modalConfig.dynamic.addQuoteNote(quoteId)).afterClosed().pipe(
      take(1),
    ).subscribe();
  };
}
